import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import { Compass, BookOpen, ClipboardCheck, Award, ArrowRight, Sparkles, ShieldCheck, Users, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import CategoryCard from "../components/CategoryCard";

const STEPS = [
  { icon: Compass, label: "Je choisis ma catégorie", text: "8 univers, 38 certifications — choisissez ce qui vous parle." },
  { icon: BookOpen, label: "Je suis mes cours", text: "Leçons claires, vocabulaire clé, format court et efficace." },
  { icon: ClipboardCheck, label: "Je réponds aux quiz", text: "5 quiz par certification, score requis : 70%." },
  { icon: Award, label: "Je télécharge mon certificat", text: "PDF élégant + code unique vérifiable par QR." },
];

const TESTIMONIALS = [
  { name: "Camille R.", role: "Conseillère bancaire", text: "Enfin une plateforme qui parle finance sans jargon prétentieux. J'ai bouclé la certif en 3 soirées." },
  { name: "Yannis B.", role: "Étudiant en école d'art", text: "La catégorie Art est passionnante. Le certificat fait son effet sur LinkedIn." },
  { name: "Léa M.", role: "Coach sportive", text: "Le module nutrition est un excellent complément à ma pratique. La distinction théorique/officielle est très claire." },
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [catState, setCatState] = useState("loading"); // loading | ready | error
  useEffect(() => {
    let cancelled = false;
    setCatState("loading");
    api.get("/categories")
      .then((r) => { if (!cancelled) { setCategories(r.data || []); setCatState("ready"); } })
      .catch(() => { if (!cancelled) setCatState("error"); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-gradient-to-b from-ink-50 to-white border-b border-ink-100">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-[1.1fr,0.9fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-50 border border-gold-200 text-xs font-mono text-gold-700 uppercase tracking-widest mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Nouvelle promotion — Bienvenue
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-ink-900">
              Apprenez. Validez. <span className="text-gold-gradient">Certifiez.</span>
            </h1>
            <p className="mt-5 text-lg text-ink-600 max-w-2xl">
              Aurum Academy est née d'une conviction simple : le savoir de qualité ne devrait pas être réservé à une élite.
            </p>
            <p className="mt-4 text-sm text-ink-500 max-w-2xl leading-relaxed">
              Nos certifications rendent accessibles des connaissances souvent enfermées dans les grandes écoles, sans barrière sociale ni prix prohibitif. Pour 45 à 75 €, vous accédez à une vraie certification, dans un domaine qui vous passionne.

Choisissez une catégorie qui vous intéresse, suivez des cours clairs, répondez au quiz, et repartez avec un certificat qui prouve que vous savez de quoi vous parlez.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/categories" className="px-6 py-3 rounded-lg bg-gold-500 text-white font-semibold hover:bg-gold-600 shadow-gold transition inline-flex items-center gap-2" data-testid="hero-cta-primary">
                Découvrir les certifications <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#how-it-works" className="px-6 py-3 rounded-lg border border-ink-200 text-ink-800 hover:bg-ink-50 font-medium transition" data-testid="hero-cta-secondary">
                Comment ça marche ?
              </a>
            </div>
            <div className="mt-8 flex items-center gap-4 text-xs text-ink-500">
              <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-success" /> Accès à vie</span>
              <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-success" /> Certificat PDF</span>
              <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-success" /> Remboursable 48h</span>
            </div>
          </div>

          {/* Simple illustration card */}
          <div className="relative">
            <div className="surface rounded-2xl p-6 md:p-8 shadow-pop">
              <div className="flex items-center gap-3 pb-4 border-b border-ink-100">
                <div className="w-10 h-10 rounded-lg bg-gold-50 border border-gold-200 flex items-center justify-center">
                  <Award className="w-5 h-5 text-gold-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-ink-900">Certificat E.C.S</div>
                  <div className="text-xs text-ink-500">Finance & Patrimoine · Débutant</div>
                </div>
              </div>
              <div className="pt-4 space-y-3">
                {[
                  ["Module 1 — Introduction", true],
                  ["Module 2 — Approfondissement", true],
                  ["Module 3 — Synthèse", true],
                  ["Quiz final — 5×20 questions", true],
                  ["Certificat PDF + QR code", true],
                ].map(([label, done]) => (
                  <div key={label} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className={`w-4 h-4 ${done ? "text-success" : "text-ink-300"}`} />
                    <span className={done ? "text-ink-800" : "text-ink-500"}>{label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-between rounded-lg bg-gold-50 border border-gold-200 px-4 py-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gold-700 font-mono">Prix</div>
                  <div className="font-display text-2xl font-bold text-gold-gradient">45€</div>
                </div>
                <span className="text-xs text-gold-700 font-medium">Paiement unique · accès à vie</span>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 rotate-2 surface rounded-xl px-3 py-2 shadow-card hidden md:flex items-center gap-2 text-xs">
              <div className="w-6 h-6 rounded-full bg-success/15 flex items-center justify-center">
                <ShieldCheck className="w-3.5 h-3.5 text-success" />
              </div>
              Vérifiable par QR
            </div>
            <div className="absolute -bottom-3 -left-3 -rotate-2 surface rounded-xl px-3 py-2 shadow-card hidden md:flex items-center gap-2 text-xs">
              <div className="w-6 h-6 rounded-full bg-gold-100 flex items-center justify-center">
                <Users className="w-3.5 h-3.5 text-gold-700" />
              </div>
              5 800 apprenants
            </div>
          </div>
        </div>
      </section>

      {/* Trust counters */}
      <section className="max-w-7xl mx-auto px-6 -mt-8">
        <div className="surface rounded-2xl grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-ink-100">
          {[
            { key: "certs", v: 1240, suf: "+", label: "certifications délivrées" },
            { key: "learners", v: 5800, suf: "+", label: "apprenants inscrits" },
            { key: "rating", v: 4.8, suf: " / 5", label: "satisfaction moyenne", dec: 1 },
          ].map((c, i) => (
            <div key={c.key} className="p-6 text-center" data-testid={`trust-counter-${i}`}>
              <div className="font-display text-4xl md:text-5xl font-bold text-ink-900">
                <CountUp end={c.v} duration={2.2} decimals={c.dec || 0} suffix={c.suf} enableScrollSpy scrollSpyOnce />
              </div>
              <div className="text-xs uppercase tracking-widest text-ink-500 mt-2 font-medium">{c.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-10">
          <div className="text-xs uppercase tracking-widest text-gold-700 font-mono font-semibold mb-3">Notre méthode</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900">Comment ça marche ?</h2>
          <p className="text-ink-600 mt-3">Quatre étapes, zéro prise de tête.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.label} className="surface rounded-2xl p-6 relative" data-testid={`step-${i+1}`}>
              <div className="absolute top-4 right-4 font-mono text-xs text-ink-300 font-bold">0{i+1}</div>
              <div className="w-12 h-12 rounded-xl bg-gold-50 border border-gold-200 flex items-center justify-center mb-4">
                <s.icon className="w-6 h-6 text-gold-700" />
              </div>
              <h3 className="font-display text-lg font-semibold text-ink-900 mb-1.5">{s.label}</h3>
              <p className="text-sm text-ink-600">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="gold-divider max-w-5xl mx-auto" />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-gold-700 font-mono font-semibold mb-3">Explorer</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900">Choisissez votre univers</h2>
          </div>
          <Link to="/categories" className="text-gold-700 hover:text-gold-800 font-medium inline-flex items-center gap-1 text-sm" data-testid="home-view-all-categories">
            Voir toutes les catégories <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="home-categories-grid">
          {catState === "loading" && Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-ink-100 bg-ink-50/60 h-64 animate-pulse" data-testid="home-category-skeleton" />
          ))}
          {catState === "error" && (
            <div className="col-span-full text-center py-12">
              <p className="text-ink-700 text-sm">Impossible de charger les catégories.</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-gold-700 hover:text-gold-800 text-sm underline"
                data-testid="home-categories-retry"
              >
                Réessayer
              </button>
            </div>
          )}
          {catState === "ready" && categories.map((c, i) => <CategoryCard key={c.id} category={c} index={i} />)}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-ink-50 border-y border-ink-100">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-widest text-gold-700 font-mono font-semibold mb-3">Témoignages</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900">Ils ont passé le cap</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t) => (
              <figure key={t.name} className="surface rounded-2xl p-6">
                <div className="text-gold-500 font-display text-4xl leading-none mb-3">"</div>
                <blockquote className="text-ink-800 text-sm leading-relaxed">{t.text}</blockquote>
                <figcaption className="mt-5 text-sm">
                  <div className="font-semibold text-ink-900">{t.name}</div>
                  <div className="text-ink-500 text-xs">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="max-w-5xl mx-auto px-6 py-24">
        <div className="rounded-3xl p-10 md:p-14 text-center bg-gradient-to-br from-gold-50 via-white to-gold-50 border border-gold-200">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900">Prêt(e) à devenir vraiment incollable ?</h2>
          <p className="text-ink-600 mt-4 max-w-xl mx-auto">
            Créez votre compte gratuit, explorez les leçons en accès libre, et certifiez-vous quand vous serez prêt(e).
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/register" className="px-6 py-3 rounded-lg bg-gold-500 text-white font-semibold hover:bg-gold-600 shadow-gold inline-flex items-center justify-center gap-2" data-testid="home-cta-register">
              Créer mon compte <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/categories" className="px-6 py-3 rounded-lg border border-ink-200 bg-white text-ink-800 hover:bg-ink-50 font-medium" data-testid="home-cta-explore">
              Explorer les catégories
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
